import { createIntl, createIntlCache, defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';
import { getLocaleMessages, hasLocaleMessages } from '../../i18n/locale-messages';
import type { SavedSearchSummary } from '../../types/saved-search';
import { getRegionTagLabel } from '../facets/region-tag-labels';
import { HYBRID_VALUES, REMOTE_VALUES } from '../facets/format-filter-label';
import { getKnownSingleValueFilterLabel } from '../facets/single-value-filter-label';
import { savedSearchLocale, type AppLocale } from './edit-saved-search-url';

/**
 * Everything the hub says ABOUT a saved search is said in the search's own language, not the
 * viewer's: a /fi/tyopaikat search carries Finnish facet values ("Etätyö", "Kokoaikainen"),
 * so its generated name and attribute labels must be Finnish too, or the card reads as a
 * mixed-language sentence. This module formats through a per-locale intl instance built from
 * the compiled catalogue, independent of the page's IntlProvider.
 *
 * In the browser a search's locale may not be loaded yet (only the page's own is preloaded);
 * components that render these call useLocaleMessagesFor() to fetch it, and until it arrives
 * formatting falls back to the English defaultMessage, uncached so the real one is picked up after.
 */

const cache = createIntlCache();
const intlByLocale = new Map<AppLocale, IntlShape>();

export function intlForLocale(locale: AppLocale): IntlShape {
  const cached = intlByLocale.get(locale);
  if (cached) return cached;
  const loaded = hasLocaleMessages(locale);
  const intl = createIntl(
    { locale, defaultLocale: 'en', messages: loaded ? getLocaleMessages(locale) : {}, onError: () => {} },
    cache,
  );
  if (loaded) intlByLocale.set(locale, intl);
  return intl;
}

export function formatInLocale(locale: AppLocale, descriptor: MessageDescriptor, values?: Record<string, string | number>): string {
  return intlForLocale(locale).formatMessage(descriptor, values);
}

/** Attribute names as they appear in stored search URLs, labelled in every locale. */
export const attributeLabels = defineMessages({
  location: { id: 'savedSearches.attr.location', defaultMessage: 'Location', description: 'Saved-search chip label for the legacy location filter' },
  regionTags: { id: 'savedSearches.attr.regionTags', defaultMessage: 'Location', description: 'Saved-search chip label for the region/city filter' },
  field: { id: 'savedSearches.attr.field', defaultMessage: 'Field', description: 'Saved-search chip label for the professional field filter' },
  jobType: { id: 'savedSearches.attr.jobType', defaultMessage: 'Job type', description: 'Saved-search chip label for the employment type filter' },
  normalisedRoleLevel: { id: 'savedSearches.attr.normalisedRoleLevel', defaultMessage: 'Level', description: 'Saved-search chip label for the seniority level filter' },
  normalisedTitle: { id: 'savedSearches.attr.normalisedTitle', defaultMessage: 'Role', description: 'Saved-search chip label for the normalised job title filter' },
  remoteness: { id: 'savedSearches.attr.remoteness', defaultMessage: 'Work mode', description: 'Saved-search chip label for the remote / hybrid / on-site filter' },
  benefits: { id: 'savedSearches.attr.benefits', defaultMessage: 'Benefits', description: 'Saved-search chip label for the benefits filter' },
  companyId: { id: 'savedSearches.attr.companyId', defaultMessage: 'Company', description: 'Saved-search chip label for the company filter' },
  companyIndustry: { id: 'savedSearches.attr.companyIndustry', defaultMessage: 'Industry', description: 'Saved-search chip label for the company industry filter' },
  educationLevel: { id: 'savedSearches.attr.educationLevel', defaultMessage: 'Education', description: 'Saved-search chip label for the education level filter' },
  skillsAndTechnologies: { id: 'savedSearches.attr.skillsAndTechnologies', defaultMessage: 'Skills', description: 'Saved-search chip label for the skills and technologies filter' },
  requiredLanguages: { id: 'savedSearches.attr.requiredLanguages', defaultMessage: 'Languages', description: 'Saved-search chip label for the required languages filter' },
  optionalLanguages: { id: 'savedSearches.attr.optionalLanguages', defaultMessage: 'Nice-to-have languages', description: 'Saved-search chip label for the optional languages filter' },
  peopleManagementTier: { id: 'savedSearches.attr.peopleManagementTier', defaultMessage: 'Management', description: 'Saved-search chip label for the people-management tier filter' },
  isSummerJob: { id: 'savedSearches.attr.isSummerJob', defaultMessage: 'Summer job', description: 'Saved-search chip label for the summer-job filter' },
  headcount: { id: 'savedSearches.attr.headcount', defaultMessage: 'Company size', description: 'Saved-search chip label for the company headcount filter' },
});

const nameMessages = defineMessages({
  jobsAt: { id: 'savedSearches.name.jobsAt', defaultMessage: 'Jobs at {company}', description: 'Generated saved-search name when the search is a single company with no keywords' },
  fieldJobs: { id: 'savedSearches.name.fieldJobs', defaultMessage: '{field} jobs', description: 'Generated saved-search name when the search is a professional field with no keywords, e.g. "Nursing jobs"' },
  roleJobs: { id: 'savedSearches.name.roleJobs', defaultMessage: '{role} jobs', description: 'Generated saved-search name from a normalised job title filter, e.g. "Data engineer jobs"' },
  levelJobs: { id: 'savedSearches.name.levelJobs', defaultMessage: '{level} jobs', description: 'Generated saved-search name from a seniority level only, e.g. "Senior jobs"' },
  typeJobs: { id: 'savedSearches.name.typeJobs', defaultMessage: '{type} jobs', description: 'Generated saved-search name from an employment type only, e.g. "Part-time jobs"' },
  summerJobs: { id: 'savedSearches.name.summerJobs', defaultMessage: 'Summer jobs', description: 'Generated saved-search name for a summer-job filter with no keywords' },
  allJobs: { id: 'savedSearches.name.allJobs', defaultMessage: 'All jobs', description: 'Generated saved-search name when the search has no keywords and no defining filter' },
  inPlace: { id: 'savedSearches.name.inPlace', defaultMessage: '{name} in {place}', description: 'Generated saved-search name with a location appended, e.g. "Nurse in Helsinki"' },
  remotePrefix: { id: 'savedSearches.name.remotePrefix', defaultMessage: 'Remote {name}', description: 'Generated saved-search name for a remote-only search, e.g. "Remote nurse in Helsinki"' },
  hybridPrefix: { id: 'savedSearches.name.hybridPrefix', defaultMessage: 'Hybrid {name}', description: 'Generated saved-search name for a hybrid-only search' },
  worldwide: { id: 'savedSearches.name.worldwide', defaultMessage: '{name}, worldwide', description: 'Generated saved-search name suffix when the Finland-only scope is off' },
});

function firstValue(summary: SavedSearchSummary, attribute: string): string | undefined {
  return summary.facets.find((f) => f.attribute === attribute)?.values[0];
}

function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

/** Localized place label: a regionTags placeId resolves through the gazetteer, a legacy location string is used as-is. */
function placeLabel(summary: SavedSearchSummary, locale: AppLocale): string | undefined {
  const tags = summary.regionTagsValues.map((v) => getRegionTagLabel(v, locale)).filter(Boolean);
  const places = tags.length > 0 ? tags : summary.locationValues;
  if (places.length === 0) return undefined;
  if (places.length <= 2) return places.join(', ');
  return `${places.slice(0, 2).join(', ')} +${places.length - 2}`;
}

/**
 * Natural-language default name for a saved search, in the search's own locale:
 * "Remote nurse in Helsinki", "Jobs at Supercell", "Hoitaja, Uusimaa · Kokoaikainen".
 * A user-set label always wins over this; it is only the fallback for unnamed searches.
 */
export function buildSavedSearchName(summary: SavedSearchSummary, storedUrl: string): string {
  const locale = savedSearchLocale(storedUrl);
  const t = (d: MessageDescriptor, v?: Record<string, string | number>) => formatInLocale(locale, d, v);

  const query = summary.query?.trim();
  const company = firstValue(summary, 'companyId');
  const field = firstValue(summary, 'field');
  const role = firstValue(summary, 'normalisedTitle');
  const level = firstValue(summary, 'normalisedRoleLevel');
  const type = firstValue(summary, 'jobType');
  const summer = firstValue(summary, 'isSummerJob');
  const remoteness = (firstValue(summary, 'remoteness') ?? '').toLowerCase();

  // The subject: what kind of job. Later parts hang off it.
  let name: string;
  const usedAttributes = new Set<string>();
  if (query) name = query;
  else if (company) {
    name = t(nameMessages.jobsAt, { company });
    usedAttributes.add('companyId');
  } else if (role) {
    name = t(nameMessages.roleJobs, { role });
    usedAttributes.add('normalisedTitle');
  } else if (field) {
    name = t(nameMessages.fieldJobs, { field });
    usedAttributes.add('field');
  } else if (summer) {
    name = t(nameMessages.summerJobs);
    usedAttributes.add('isSummerJob');
  } else if (level) {
    name = t(nameMessages.levelJobs, { level });
    usedAttributes.add('normalisedRoleLevel');
  } else if (type) {
    name = t(nameMessages.typeJobs, { type });
    usedAttributes.add('jobType');
  } else name = t(nameMessages.allJobs);

  if (REMOTE_VALUES.has(remoteness)) {
    name = t(nameMessages.remotePrefix, { name });
    usedAttributes.add('remoteness');
  } else if (HYBRID_VALUES.has(remoteness)) {
    name = t(nameMessages.hybridPrefix, { name });
    usedAttributes.add('remoteness');
  }

  const place = placeLabel(summary, locale);
  if (place) name = t(nameMessages.inPlace, { name, place });
  else if (summary.isGlobalScope) name = t(nameMessages.worldwide, { name });

  // At most two qualifiers, as their own localized facet values: "· Senior · Full-time".
  const qualifiers: string[] = [];
  for (const attribute of ['normalisedRoleLevel', 'jobType', 'field'] as const) {
    if (usedAttributes.has(attribute)) continue;
    const value = firstValue(summary, attribute);
    if (value) qualifiers.push(value);
    if (qualifiers.length === 2) break;
  }

  return capitalize([name, ...qualifiers].join(' · '));
}

/** One chip's text, labelled in the search's locale: "Työsuhteen tyyppi: Kokoaikainen". */
export function describeFacetChip(attribute: string, values: string[], locale: AppLocale): string {
  const intl = intlForLocale(locale);
  const single = values.length === 1 ? getKnownSingleValueFilterLabel(attribute, values[0], intl.formatMessage) : null;
  if (single) return single;
  const bare = attribute.replace(/^(exclude_|only_)/, '');
  const known = (attributeLabels as Record<string, MessageDescriptor>)[bare === 'companyHeadcountRangeMax' ? 'headcount' : bare];
  const label = known
    ? intl.formatMessage(known)
    : bare.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
  const prefix = attribute.startsWith('exclude_') ? '− ' : '';
  return `${prefix}${label}: ${values.join(', ')}`;
}
